import { useEffect, useRef } from 'react';

export function useHotkeyCombo(keys: string[], callback: () => void) {
	const pressed = useRef<Set<string>>(new Set());

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			pressed.current.add(e.key.toLowerCase());

			const allPressed = keys.every((key) =>
				pressed.current.has(key.toLowerCase())
			);

			if (allPressed) {
				callback();
				e.stopPropagation();
			}
		};

		const handleKeyUp = (e: KeyboardEvent) => {
			pressed.current.delete(e.key.toLowerCase());
		};

		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
		};
	}, [keys.join(','), callback]);
}

export function useHotkeyHoldingCombo(
	initialCombo: string[],
	holdKey: string,
	onActivate: () => void,
	onRelease: () => void,
	triggerCombos: { keys: string[]; callback: () => void }[]
) {
	const pressed = useRef<Set<string>>(new Set());
	const isHolding = useRef(false);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			const key = e.key.toLowerCase();
			pressed.current.add(key);

			// Check initial combo activation
			const initialComboPressed = initialCombo.every((k) =>
				pressed.current.has(k.toLowerCase())
			);

			if (initialComboPressed && !isHolding.current) {
				onActivate();
				isHolding.current = true;
				e.stopPropagation();
				e.stopImmediatePropagation();
				return; // don't check trigger combos if initial combo is pressed
			}

			// Check trigger combos while holding
			if (isHolding.current && pressed.current.has(holdKey.toLowerCase())) {
				// Sort triggerCombos so that longer key combinations are checked first
				const sortedTriggerCombos = [...triggerCombos].sort(
					(a, b) => b.keys.length - a.keys.length
				);

				for (const { keys, callback } of sortedTriggerCombos) {
					const triggerComboPressed = keys.every((k) =>
						pressed.current.has(k.toLowerCase())
					);

					if (triggerComboPressed) {
						callback();
						e.stopPropagation();
						e.stopImmediatePropagation();
						break;
					}
				}
			}
		};

		const handleKeyUp = (e: KeyboardEvent) => {
			const key = e.key.toLowerCase();
			pressed.current.delete(key);

			// Check if hold key was released
			if (key === holdKey.toLowerCase() && isHolding.current) {
				onRelease();
				isHolding.current = false;
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
		};
	}, [initialCombo, holdKey, onActivate, onRelease, triggerCombos]);
}
